import api from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { toast } from "sonner";

/**
 * RowSplitDialog — modal spawned by the row-level "Split shipment…"
 * action. Line items are distributed round-robin across N children.
 * Persists via POST /dropship/orders/{id}/split {parts}.
 */
export default function RowSplitDialog({
  rowSplitOid, setRowSplitOid,
  rowSplitParts, setRowSplitParts,
  orders,
  setRowActionBusy, reload,
}) {
  if (!rowSplitOid) return null;

  const save = async () => {
    const target = (orders || []).find(x => x.id === rowSplitOid);
    const lines = (target?.line_items && target.line_items.length)
      ? target.line_items
      : [{ sku: target?.sku, name: target?.product_name, qty: target?.qty || 1 }];
    const parts = Array.from({ length: rowSplitParts }, () => ({ line_items: [] }));
    lines.forEach((li, i) => { parts[i % rowSplitParts].line_items.push(li); });
    setRowActionBusy(true);
    try {
      const { data } = await api.post(`/dropship/orders/${rowSplitOid}/split`, { parts });
      toast.success(`Split into ${data.children?.length || rowSplitParts} orders`);
      setRowSplitOid(null); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Split failed");
    } finally { setRowActionBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setRowSplitOid(null)}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="row-split-dialog">
        <div className="text-[14px] font-semibold">Split shipment</div>
        <p className="text-[11.5px] text-foreground/60">Break this order into N child orders. Line items are distributed round-robin — edit them from the child rows afterward.</p>
        <div className="flex items-center gap-2">
          <label className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/55">Parts</label>
          <Input type="number" min="2" max="6" value={rowSplitParts}
            onChange={e => setRowSplitParts(Math.max(2, Math.min(6, Number(e.target.value) || 2)))}
            className="h-10 mono w-24" data-testid="row-split-parts"/>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => setRowSplitOid(null)}
            className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
            Cancel
          </button>
          <button
            onClick={save}
            data-testid="row-split-save"
            className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
            Split
          </button>
        </div>
      </div>
    </div>
  );
}
